import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import {
  LayoutGrid,
  Trophy,
  ReceiptText,
  CalendarDays,
  Clock3,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  UserRound
} from 'lucide-react'
import { auth } from '../firebase'
import { useFacility } from '../context/FacilityContext'
import Logo from './Logo'
import { initials } from './ui'
import './PartnerNav.css'

const MENU = [
  { path: '/', icon: LayoutGrid, label: 'Oversikt', match: (p) => p === '/' },
  { path: '/tournaments', icon: Trophy, label: 'Turneringer', match: (p) => p === '/tournaments' || p.startsWith('/tournaments/') },
  { path: '/sales', icon: ReceiptText, label: 'Salg', match: (p) => p.startsWith('/sales') },
  { path: '/activities', icon: CalendarDays, label: 'Aktiviteter', match: (p) => p.startsWith('/activities') },
  { path: '/rentals', icon: Clock3, label: 'Utleie', match: (p) => p.startsWith('/rentals') }
]

const useCurrentUser = () => {
  const [user, setUser] = useState(() => auth?.currentUser || null)
  useEffect(() => {
    if (!auth) return undefined
    return onAuthStateChanged(auth, (u) => setUser(u))
  }, [])
  return user
}

const PartnerNav = ({ isCollapsed, onToggle, onLogout }) => {
  const { facilities, activeFacility, activeFacilityId, setActiveFacilityId } = useFacility()
  const location = useLocation()
  const user = useCurrentUser()
  const userName = user?.displayName || user?.phoneNumber || 'Innlogget'
  const facilityName = activeFacility?.name || ''
  const listRef = useRef(null)

  // På mobil er menyen en horisontal rad: sørg for at aktivt valg er synlig.
  useEffect(() => {
    const list = listRef.current
    const el = list?.querySelector('.pnav-item.active')
    if (!list || !el || list.scrollWidth <= list.clientWidth) return
    list.scrollLeft = el.offsetLeft - (list.clientWidth - el.offsetWidth) / 2
  }, [location.pathname])

  return (
    <aside className={`pnav ${isCollapsed ? 'collapsed' : ''}`} aria-label="Partnerportal">
      <div className="pnav-top">
        <NavLink to="/" className="pnav-brand" aria-label="Spelaren Partner – til oversikten">
          {isCollapsed ? <Logo variant="mark" height={30} /> : <Logo variant="full" height={26} />}
          {!isCollapsed && <span className="pnav-badge">PARTNER</span>}
        </NavLink>
        <div className="pnav-mobile-actions">
          <button type="button" className="icon-btn sm" onClick={onLogout} aria-label="Logg ut" title="Logg ut">
            <LogOut size={16} strokeWidth={1.9} aria-hidden="true" />
          </button>
        </div>
      </div>

      {facilities.length > 0 && (
        isCollapsed ? (
          <div className="pnav-arena-mini" title={facilityName}>
            <span className="avatar avatar-ink" aria-hidden="true">{facilityName.charAt(0)}</span>
            <span className="sr-only">Aktiv arena: {facilityName}</span>
          </div>
        ) : (
          <label className="pnav-arena">
            <span className="pnav-label">Aktiv arena</span>
            <span className="pnav-arena-box">
              <span className="avatar avatar-ink" aria-hidden="true">{facilityName.charAt(0)}</span>
              <select
                aria-label="Velg arena"
                value={activeFacilityId || ''}
                onChange={(e) => setActiveFacilityId(e.target.value)}
                disabled={facilities.length < 2}
              >
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
              {facilities.length > 1 && <ChevronDown size={16} strokeWidth={2} aria-hidden="true" className="pnav-arena-chev" />}
            </span>
          </label>
        )
      )}

      <nav className="pnav-list" aria-label="Hovedmeny" ref={listRef}>
        {MENU.map((item) => {
          const Icon = item.icon
          const active = item.match(location.pathname)
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`pnav-item ${active ? 'active' : ''}`}
              aria-current={active ? 'page' : undefined}
              title={isCollapsed ? item.label : undefined}
            >
              <Icon size={19} strokeWidth={1.9} aria-hidden="true" />
              <span className="pnav-item-label">{item.label}</span>
            </NavLink>
          )
        })}
      </nav>

      <div className="pnav-foot">
        {!isCollapsed && (
          <div className="pnav-user">
            <span className="avatar" aria-hidden="true">{user?.displayName ? initials(user.displayName) : <UserRound size={17} strokeWidth={1.9} />}</span>
            <div className="pnav-user-text">
              <span className="pnav-user-name">{userName}</span>
              <span className="pnav-user-role">{activeFacility?.type || 'Partner'}</span>
            </div>
          </div>
        )}
        <button type="button" className="pnav-logout" onClick={onLogout} title={isCollapsed ? 'Logg ut' : undefined}>
          <LogOut size={17} strokeWidth={1.9} aria-hidden="true" />
          <span className="pnav-item-label">Logg ut</span>
        </button>
        <button
          type="button"
          className="pnav-collapse"
          onClick={onToggle}
          aria-label={isCollapsed ? 'Vis hele menyen' : 'Skjul menyen'}
          aria-expanded={!isCollapsed}
          title={isCollapsed ? 'Vis hele menyen' : 'Skjul menyen'}
        >
          {isCollapsed ? <PanelLeftOpen size={18} strokeWidth={1.9} aria-hidden="true" /> : <PanelLeftClose size={18} strokeWidth={1.9} aria-hidden="true" />}
          <span className="pnav-item-label">Skjul meny</span>
        </button>
      </div>
    </aside>
  )
}

export default PartnerNav
